import { userContext } from '../userManager';

export const apiUserGet = (id) => userContext.apiCall({ url: `/users/${id}` });

export const apiAccountsGet = () => userContext.apiCall({ url: `/accounts` });

export const apiAccountGet = (accountId) => userContext.apiCall({ url: `/accounts/${accountId}` });

export const apiAccountPost = ({
  name,
  accountTypeId,
  ownerId,
  financialAdvisorId,
  beneficiaryName,
  beneficiaryLastName,
}) =>
  userContext.apiCall({
    url: `/accounts`,
    method: 'POST',
    body: {
      name,
      accountTypeId,
      ownerId,
      financialAdvisorId,
      beneficiaryName,
      beneficiaryLastName,
    },
  });

export const apiAccountRiskAppetiteProfilesGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/risk-appetite-profiles`,
  });

export const apiAccountRiskAppetiteProfilePost = (
  accountId,
  { userId, volSensibilityLevel, riskSensibilityLevel }
) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/risk-appetite-profiles`,
    method: 'POST',
    body: { userId, volSensibilityLevel, riskSensibilityLevel },
  });

export const apiAccountConfirmInfo = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/confirm-info`,
    method: 'GET',
  });

export const apiAccountContractsGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/contracts`,
    method: 'GET',
  });

export const apiAccountContractsSignPostOtp = (accountId, ids, otp = null, alias = null) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/contracts/sign`,
    method: 'POST',
    body: { contractIds: ids, otp, alias },
  });

export const apiAccountInvestmentAgreementsGet = () =>
  userContext.apiCall({
    url: `/accounts/investment-agreements`,
  });

export const apiAccountSignedInvestmentAgreementsGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/signed-investment-agreements`,
  });

export const apiAccountInvestmentAgreementsPost = (accountId, { contractId, otp = null }) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/investment-agreements`,
    method: 'POST',
    noResBody: true,
    body: { contractId, otp },
  });

export const apiAccountRiskAppetiteProfilePatch = (
  id,
  { volSensibilityLevel, riskSensibilityLevel }
) =>
  userContext.apiCall({
    url: `/risk-appetite-profiles/${id}`,
    method: 'PATCH',
    body: { volSensibilityLevel, riskSensibilityLevel },
  });

export const apiAccountInvestmentObjectivesGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/investment-objectives`,
  });

export const apiAccountInvestmentObjectivesPost = (
  accountId,
  {
    userId,
    initialInvestment,
    investmentPeriod,
    averageYearlyInvestment,
    title,
    typeId,
    targetAmount,
    retirementStartAge,
    retireeDateOfBirth,
    desiredMonthlyIncome,
  }
) => {
  const showDetails = retirementStartAge && retireeDateOfBirth && desiredMonthlyIncome;

  return userContext.apiCall({
    url: `/accounts/${accountId}/investment-objectives`,
    method: 'POST',
    body: {
      userId,
      initialInvestment,
      investmentPeriod,
      averageYearlyInvestment,
      title,
      typeId,
      targetAmount,
      details: showDetails
        ? {
            retirementStartAge: Number(retirementStartAge),
            retireeDateOfBirth: retireeDateOfBirth && `${retireeDateOfBirth}-01-01`,
            desiredMonthlyIncome: Number(desiredMonthlyIncome),
          }
        : undefined,
    },
  });
};

export const apiAccountInvestmentObjectivesPatch = (
  id,
  { initialInvestment, investmentPeriod, averageYearlyInvestment }
) =>
  userContext.apiCall({
    url: `/investment-objectives/${id}`,
    method: 'PATCH',
    body: {
      initialInvestment,
      investmentPeriod,
      averageYearlyInvestment,
    },
  });

export const apiAccountFinancialAssetsGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/financial-assets`,
  });

export const apiAccountFinancialAssetsPost = (
  accountId,
  {
    userId,
    annualNetIncome,
    annualIncomeGrowthRate,
    investmentShareOfTotalAssets,
    savingsShareOfNetIncome,
  }
) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/financial-assets`,
    method: 'POST',
    body: {
      userId,
      annualNetIncome,
      annualIncomeGrowthRate,
      investmentShareOfTotalAssets,
      savingsShareOfNetIncome,
    },
  });

export const apiAccountFinancialAssetsPatch = (
  id,
  { annualNetIncome, annualIncomeGrowthRate, investmentShareOfTotalAssets, savingsShareOfNetIncome }
) =>
  userContext.apiCall({
    url: `/financial-assets/${id}`,
    method: 'PATCH',
    body: {
      annualNetIncome,
      annualIncomeGrowthRate,
      investmentShareOfTotalAssets,
      savingsShareOfNetIncome,
    },
  });

export const apiAccountLifestyleCostsGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/lifestyle-costs`,
  });

export const apiAccountLifestyleCostsPost = (
  accountId,
  { userId, annualFixedExpenses, insuranceCoverageLevel }
) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/lifestyle-costs`,
    method: 'POST',
    body: {
      userId,
      annualFixedExpenses,
      insuranceCoverageLevel,
    },
  });

export const apiAccountLifestyleCostsPatch = (
  id,
  { annualFixedExpenses, insuranceCoverageLevel }
) =>
  userContext.apiCall({
    url: `/lifestyle-costs/${id}`,
    method: 'PATCH',
    body: { annualFixedExpenses, insuranceCoverageLevel },
  });

export const apiAccountFinancialExperiencesGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/financial-experiences`,
  });

export const apiAccountFinancialExperiencesPost = (
  accountId,
  {
    userId,
    financialServicesExperience,
    financialNewsTrackingLevel,
    financialIndustryWorkExperience,
    tradedInstruments,
    financialAssetsValue,
    hasBeenInvestingFor,
    tradeFrequency,
    savingsOrigin,
    areBondsRiskFree,
    hyBondIsA,
    complexInstrumentsAre,
    areEquitiesRiskFree,
    equityValueDependsOn,
    certificatesAre,
    truthStatement,
    illiquidInstrumentsAreHardToSell,
  }
) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/financial-experiences`,
    method: 'POST',
    body: {
      userId,
      financialServicesExperience,
      financialNewsTrackingLevel,
      financialIndustryWorkExperience,
      tradedInstruments,
      financialAssetsValue,
      hasBeenInvestingFor,
      tradeFrequency,
      savingsOrigin,
      areBondsRiskFree,
      hyBondIsA,
      complexInstrumentsAre,
      areEquitiesRiskFree,
      equityValueDependsOn,
      certificatesAre,
      truthStatement,
      illiquidInstrumentsAreHardToSell,
    },
  });

export const apiAccountFinancialExperiencesPatch = (
  id,
  {
    financialServicesExperience,
    financialNewsTrackingLevel,
    financialIndustryWorkExperience,
    tradedInstruments,
    financialAssetsValue,
    hasBeenInvestingFor,
    tradeFrequency,
    savingsOrigin,
    areBondsRiskFree,
    hyBondIsA,
    complexInstrumentsAre,
    areEquitiesRiskFree,
    equityValueDependsOn,
    certificatesAre,
    truthStatement,
    illiquidInstrumentsAreHardToSell,
  }
) =>
  userContext.apiCall({
    url: `/financial-experiences/${id}`,
    method: 'PATCH',
    body: {
      financialServicesExperience,
      financialNewsTrackingLevel,
      financialIndustryWorkExperience,
      tradedInstruments,
      financialAssetsValue,
      hasBeenInvestingFor,
      tradeFrequency,
      savingsOrigin,
      areBondsRiskFree,
      hyBondIsA,
      complexInstrumentsAre,
      areEquitiesRiskFree,
      equityValueDependsOn,
      certificatesAre,
      truthStatement,
      illiquidInstrumentsAreHardToSell,
    },
  });

export const apiAccountRiskProfileCalculationGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/risk-profile-calculation`,
  });

export const apiAccountRiskProfilesPost = (accountId, { riskProfileId }) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/risk-profiles`,
    method: 'POST',
    body: { riskProfileId },
  });

export const apiAccountFeesGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/fees`,
  });

export const apiAccountSignContractPost = (accountId, { contract, pin }) => {
  return userContext.apiCall({
    url: `/accounts/${accountId}/sign-contract`,
    method: 'POST',
    body: {
      contract,
      pin,
    },
  });
};

export const apiAccountOwnersGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/owners`,
  });

export const apiAccountOwnersPatch = (
  accountOwnerId,
  { civilStatus, ageRange, dependentFamilyMembers, jobStatus, education }
) =>
  userContext.apiCall({
    url: `/account-owners/${accountOwnerId}`,
    method: 'PATCH',
    body: { civilStatus, ageRange, dependentFamilyMembers, jobStatus, education },
  });

export const apiAccountMifidProfilesPost = (accountId) => {
  return userContext.apiCall({
    url: `/accounts/${accountId}/mifid-profiles`,
    method: 'POST',
  });
};

export const apiAccountAmlProfilesGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/aml-profiles`,
  });

export const apiAccountAmlProfilesPost = (accountId, amlProfile) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/aml-profiles`,
    method: 'POST',
    body: amlProfile,
  });

export const apiAccountAmlProfilesPatch = (id, amlProfile) =>
  userContext.apiCall({
    url: `/aml-profiles/${id}`,
    method: 'PATCH',
    body: amlProfile,
  });

export const apiAccountTaxSchemesGet = (accountId) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/tax-schemes`,
  });

export const apiAccountTaxSchemesPost = (accountId, taxScheme) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/tax-schemes`,
    method: 'POST',
    body: taxScheme,
  });

export const apiAccountTaxSchemesPatch = (id, taxScheme) =>
  userContext.apiCall({
    url: `/tax-schemes/${id}`,
    method: 'PATCH',
    body: taxScheme,
  });

export const apiAccountDocumentsGet = (id) =>
  userContext.apiCall({
    url: `/accounts/${id}/documents`,
    method: 'GET',
  });

export const apiAccountDocumentsPost = (accountId, { file }) =>
  userContext.apiCall({ url: `/accounts/${accountId}/documents`, method: 'POST', file });

export const apiAccountAumsGet = ({ accountId, startDate, endDate }) =>
  userContext.apiCall({
    url: `/accounts/${accountId}/aums`,
    qs: { startDate, endDate },
  });
